import { UserPlus } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { type Member, type MemberRole, ROLE_OPTIONS } from "@/features/team/api";
import { cn } from "@/lib/utils";

const ROLE_LABELS = Object.fromEntries(ROLE_OPTIONS.map((o) => [o.value, o.label]));

function initials(name: string) {
  return (
    name
      .split(" ")
      .map((part) => part[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?"
  );
}

const STATUS_STYLES: Record<Member["status"], string> = {
  active: "bg-success-soft text-success",
  invited: "bg-warning-soft text-warning",
  deactivated: "bg-muted text-muted-foreground",
};
const STATUS_LABELS: Record<Member["status"], string> = {
  active: "Active",
  invited: "Invited",
  deactivated: "Deactivated",
};

/**
 * Country-director / Staff roster for the Practitioners page tabs. Read-oriented:
 * member management (invite, role, deactivate) lives in the Team workspace, so
 * each tab points there rather than duplicating those controls.
 */
export function MemberRoster({ role, members }: { role: MemberRole; members: Member[] }) {
  const roleLabel = ROLE_LABELS[role] ?? role;
  const people = members.filter((m) => m.role === role);

  if (people.length === 0) {
    return (
      <Card className="items-center gap-3 border-dashed py-14 text-center">
        <span className="bg-accent text-info flex size-14 items-center justify-center rounded-xl">
          <UserPlus className="size-6" />
        </span>
        <div className="flex flex-col gap-1">
          <span className="text-[15px] font-semibold">No {roleLabel.toLowerCase()}s yet</span>
          <span className="text-muted-foreground max-w-xs text-[13px]">
            Invite people and assign them the {roleLabel} role from the Team workspace.
          </span>
        </div>
        <Button asChild variant="outline" size="sm">
          <Link to="/team">Manage in Team</Link>
        </Button>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {people.map((m) => (
        <Card key={m.id} className="flex-row items-center gap-4 p-5">
          <span className="bg-accent text-info font-heading flex size-12 shrink-0 items-center justify-center rounded-xl text-sm font-bold">
            {initials(m.name)}
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <span className="font-heading truncate text-[15px] font-semibold">{m.name}</span>
              {m.isPractitioner && (
                <span className="bg-accent text-info shrink-0 rounded-full px-2 py-px text-[10px] font-semibold uppercase">
                  Practitioner
                </span>
              )}
            </div>
            <span className="text-info truncate text-[13px]">{m.email}</span>
            <span className="text-muted-foreground truncate text-xs">
              {roleLabel}
              {m.defaultPractitionerName ? ` · ${m.defaultPractitionerName}` : ""}
            </span>
          </div>
          <span
            className={cn(
              "shrink-0 self-start rounded-full px-2.5 py-1 text-[11px] font-semibold",
              STATUS_STYLES[m.status],
            )}
          >
            {STATUS_LABELS[m.status]}
          </span>
        </Card>
      ))}
    </div>
  );
}
